/* =========================================================
   VP Nutrition Sidebar v2

   Every block (bottle image, variant title, and REFILL 1 / REFILL 2
   nutrition tables) is built from a single fetch to:

     {api_base_url}{SKU}

   The API is expected to return `variant_title` and `variant_image`
   on the response — that's the source of truth for the header once
   it arrives. Each SKU is fetched once and cached in-memory for the
   rest of the page session — clicking the same trigger again, or a
   different trigger for a SKU already open, never re-fetches.

   HOW TRIGGERS WORK
   ------------------
   Any element anywhere on the page can open the sidebar for a single
   SKU:

     <button
       data-vp-nutrition-toggle-v2="TEST1"
       data-vp-nutrition-variant-title="Base"
       data-vp-nutrition-variant-image="{{ variant.image | image_url }}"
     >
       View Base Nutrition Facts
     </button>

   - data-vp-nutrition-toggle-v2 (required): the SKU to open.
   - data-vp-nutrition-variant-title / -variant-image (optional):
     fallback header content, sourced from Shopify's own variant data
     wherever the trigger is rendered. Used to paint the header
     immediately, before the network request resolves, and as a
     safety net for any SKU whose API response doesn't (yet) include
     variant_title / variant_image. The API's own values always win
     once they're present — these are only ever a fallback, not an
     override.

   The sidebar shows only the requested SKU's block. Blocks for SKUs
   opened earlier stay in the DOM, hidden, so reopening one (from any
   trigger) shows it again without re-fetching.

   TABBED TRIGGERS
   ---------------
   A trigger with no single variant in mind (e.g. the bottom bar) can
   offer several SKUs as tabs instead:

     <button
       data-vp-nutrition-tabs-v2='[{"sku":"CORE-F01-USA","title":"Core"},
                                   {"sku":"MAX-F01-USA","title":"Max"}]'
       data-vp-nutrition-variant-image="..."
     >

   Tabs show in the sticky header and switch between those SKUs; the
   first tab opens by default and the rest are prefetched. A
   single-SKU trigger removes the tabs and shows just its own SKU.

   EVENT BINDING
   -------------
   This page also renders Vue components, which re-render/replace
   elements whenever their state changes — even elements with
   identical attributes come back as brand new nodes. A listener bound
   directly to one of those nodes (button.addEventListener(...)) is
   gone the instant Vue swaps it out. Both togglers and close triggers
   below are delegated on document instead: the listener lives on a
   node Vue never touches, and every click looks up the *current*
   matching element via closest(). Each delegated init function guards
   itself with a one-time flag on document.body so calling initAll()
   repeatedly (e.g. on shopify:section:load) never stacks up duplicate
   document-level listeners.
   ========================================================= */

(function () {
  // Persist across shopify:section:load re-inits (theme editor) so we
  // don't re-fetch SKUs we already have.
  var cache = (window.__vpNutritionCacheV2 = window.__vpNutritionCacheV2 || {});

  function el(selector) {
    return document.querySelector(selector);
  }

  function escapeHtml(value) {
    var div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }

  // Minimal sanitizer for the small bits of HTML the API returns
  // (ingredients / footnotes, both authored by our own backend).
  // Strips script/style tags and inline event handler attributes so
  // a compromised or misconfigured API response can't execute JS.
  function sanitizeHtml(html) {
    var wrapper = document.createElement('div');
    wrapper.innerHTML = html || '';
    wrapper.querySelectorAll('script, style').forEach(function (node) {
      node.remove();
    });
    wrapper.querySelectorAll('*').forEach(function (node) {
      Array.prototype.slice.call(node.attributes).forEach(function (attr) {
        if (/^on/i.test(attr.name)) node.removeAttribute(attr.name);
      });
    });
    return wrapper.innerHTML;
  }

  function titleCase(str) {
    return String(str || '')
      .trim()
      .replace(/\b\w/g, function (c) {
        return c.toUpperCase();
      });
  }

  function getLabels(root) {
    try {
      return JSON.parse(root.getAttribute('data-vp-nutrition-labels'));
    } catch (e) {
      return {};
    }
  }

  function getFooterData(root) {
    try {
      return JSON.parse(root.getAttribute('data-vp-nutrition-footer'));
    } catch (e) {
      return { disclaimerHtml: '' };
    }
  }

  function fetchNutrition(apiRoot, sku) {
    if (cache[sku] && cache[sku].promise) return cache[sku].promise;

    var promise = fetch(apiRoot + encodeURIComponent(sku))
      .then(function (response) {
        if (!response.ok) throw new Error('Nutrition API error: ' + response.status);
        return response.json();
      })
      .then(function (data) {
        // Treat a missing/empty payload the same as a failed
        // request — nothing to render.
        if (!data || (!data.nutritionaltable && !data.servingSize)) {
          throw new Error('Nutrition data not found for SKU: ' + sku);
        }
        cache[sku].status = 'loaded';
        cache[sku].data = data;
        return data;
      })
      .catch(function (err) {
        // Don't cache the failure — remove the entry so a later
        // click on this SKU tries the fetch again instead of being
        // stuck with nothing forever.
        delete cache[sku];
        throw err;
      });

    cache[sku] = { status: 'loading', promise: promise };
    return promise;
  }

  function buildRow(entry) {
    var name = entry.locale && entry.locale.en;
    var v = entry.values || {};
    var nrv = v.dailyValue ? escapeHtml(v.dailyValue) + '%' : '&dagger;';

    var row = document.createElement('div');
    row.className = 'vp-nutrition__row';
    row.innerHTML =
      '<div class="vp-nutrition__row-name">' + escapeHtml(name) +
        (v.serving ? '<span>' + escapeHtml(v.serving) + ' ' + escapeHtml(v.servingunit) + '</span>' : '') +
      '</div>' +
      '<div class="vp-nutrition__row-nrv">' + nrv + '</div>' +
      '<div class="vp-nutrition__row-supports">' + escapeHtml(v.benefits) + '</div>';
    return row;
  }

  function buildCard(data, labels) {
    var card = document.createElement('div');
    card.className = 'vp-nutrition__card';

    var html =
      '<h3 class="vp-nutrition__card-title">' + escapeHtml(labels.nutritionFactsTitle || 'Nutrition Facts') + '</h3>' +
      '<div class="vp-nutrition__meta-row"><span>' + escapeHtml(labels.servingSizeLabel || 'Serving Size') + '</span>' +
        '<span>' + escapeHtml(data.servingSize) + ' ' + escapeHtml(titleCase(data.servingSizeUnit)) + '</span></div>' +
      '<div class="vp-nutrition__meta-row"><span>' + escapeHtml(labels.servingsPerContainerLabel || 'Servings Per Container') + '</span>' +
        '<span>' + escapeHtml(data.servingsPerUnit) + ' Servings</span></div>' +
      '<hr class="vp-nutrition__divider">';

    card.innerHTML = html;

    var tables = data.nutritionaltable || [];
    tables.forEach(function (table, index) {
      var heading = document.createElement('div');
      heading.className = 'vp-nutrition__table-heading';
      // Only the first REFILL table shows the NRV % / SUPPORTS
      // column labels — subsequent tables just show the REFILL N
      // label since the columns beneath them are still implied.
      heading.innerHTML =
        '<span>' + escapeHtml(labels.refillPrefix || 'REFILL') + ' ' + (index + 1) + '</span>' +
        (index === 0
          ? '<span>' + escapeHtml(labels.nrvColumnLabel || 'NRV %*') + '</span>' +
            '<span>' + escapeHtml(labels.supportsColumnLabel || 'SUPPORTS') + '</span>'
          : '');
      card.appendChild(heading);

      var thinRule = document.createElement('hr');
      thinRule.className = 'vp-nutrition__divider vp-nutrition__divider--thin';
      card.appendChild(thinRule);

      table.forEach(function (entry) {
        card.appendChild(buildRow(entry));
      });

      var ingredientEntry = (data.ingredients || [])[index];
      if (ingredientEntry && ingredientEntry.locale && ingredientEntry.locale.en) {
        var ingBlock = document.createElement('div');
        ingBlock.className = 'vp-nutrition__ingredients';
        ingBlock.innerHTML =
          '<p class="vp-nutrition__ingredients-title">' + escapeHtml(labels.ingredientsLabel || 'Ingredients') + '</p>' +
          '<div class="vp-nutrition__ingredients-body">' + sanitizeHtml(ingredientEntry.locale.en) + '</div>';
        card.appendChild(ingBlock);
      }
    });

    if (data.footnotes) {
      var footDivider = document.createElement('hr');
      footDivider.className = 'vp-nutrition__divider';
      card.appendChild(footDivider);

      var foot = document.createElement('div');
      foot.className = 'vp-nutrition__footnotes';
      foot.innerHTML = sanitizeHtml(data.footnotes);
      card.appendChild(foot);
    }

    return card;
  }

  // "How to use" copy sits between the card and the trust row. The
  // API can omit `howtouse` entirely or send it with empty HTML (e.g.
  // "<p></p>"), so render nothing unless there's visible text.
  function buildHowToUse(data, labels) {
    var html = data.howtouse && data.howtouse.locale && data.howtouse.locale.en;
    if (!html) return null;

    var body = sanitizeHtml(html);
    var probe = document.createElement('div');
    probe.innerHTML = body;
    if (!probe.textContent.trim()) return null;

    var block = document.createElement('div');
    block.className = 'vp-nutrition__howto';
    block.innerHTML =
      '<p class="vp-nutrition__howto-title">' + escapeHtml(labels.howToUseLabel || 'How to use:') + '</p>' +
      '<div class="vp-nutrition__howto-body">' + body + '</div>';
    return block;
  }

  function buildHeader(data, sku, fallback) {
    fallback = fallback || {};
    var title = (data && data.variant_title) || fallback.title || sku;
    var image = (data && data.variant_image) || fallback.image;

    var header = document.createElement('div');
    header.className = 'vp-nutrition__block-header';

    var html = '';
    if (image) {
      html += '<img class="vp-nutrition__block-image" src="' + escapeHtml(image) + '" alt="' + escapeHtml(title) + '" loading="lazy">';
    }
    html += '<h2 class="vp-nutrition__block-title">' + escapeHtml(title) + '</h2>';
    header.innerHTML = html;
    return header;
  }

  // Real checkmark asset (green circle + white check), inlined so it
  // renders with zero extra requests.
  var CHECK_ICON_SVG =
    '<svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<ellipse cx="6.5" cy="6.30213" rx="6.5" ry="6.30213" fill="#0DAD63"/>' +
    '<path d="M5.49829 9.60886L2.39453 6.66809L3.34878 5.76407L5.49829 7.80071L10.1198 3.42188L11.0741 4.32589L5.49829 9.60886Z" fill="white"/>' +
    '</svg>';

  // Static — not editor-configurable. Order matches the 2-column
  // grid reading order (row 1, then row 2).
  var TRUST_ITEMS = [
    'Vegan Certified',
    'No fillers or colourants',
    'Gluten-Free',
    'No artificial Flavours'
  ];

  // Builds the trust row (two static icons + four static checkmark
  // items) and the disclaimer beneath it. The icons and checkmark
  // items are fixed content, not theme-editor blocks; only the
  // disclaimer copy comes from a section setting.
  function buildFooter(root) {
    var footerData = getFooterData(root);
    var wrapper = document.createDocumentFragment();

    var trust = document.createElement('div');
    trust.className = 'vp-nutrition__trust';

    var icons = document.createElement('div');
    icons.className = 'vp-nutrition__trust-icons';
    var labIcon = root.getAttribute('data-vp-nutrition-icon-lab');
    var ukIcon = root.getAttribute('data-vp-nutrition-icon-uk');
    var iconsHtml = '';
    if (labIcon) {
      iconsHtml += '<img src="' + escapeHtml(labIcon) + '" alt="Third Party Tested" loading="lazy">';
    }
    if (ukIcon) {
      iconsHtml += '<img src="' + escapeHtml(ukIcon) + '" alt="Produced in the UK" loading="lazy">';
    }
    icons.innerHTML = iconsHtml;
    trust.appendChild(icons);

    var items = document.createElement('div');
    items.className = 'vp-nutrition__trust-items';
    TRUST_ITEMS.forEach(function (label) {
      var item = document.createElement('div');
      item.className = 'vp-nutrition__trust-item';
      item.innerHTML =
        '<span class="vp-nutrition__trust-check" aria-hidden="true">' + CHECK_ICON_SVG + '</span>' +
        '<span class="vp-nutrition__trust-label">' + escapeHtml(label) + '</span>';
      items.appendChild(item);
    });
    trust.appendChild(items);

    wrapper.appendChild(trust);

    if (footerData.disclaimerHtml) {
      var divider = document.createElement('hr');
      divider.className = 'vp-nutrition__disclaimer-divider';
      wrapper.appendChild(divider);

      var disclaimer = document.createElement('div');
      disclaimer.className = 'vp-nutrition__disclaimer';
      disclaimer.innerHTML = sanitizeHtml(footerData.disclaimerHtml);
      wrapper.appendChild(disclaimer);
    }

    return wrapper;
  }

  function buildSkeleton(root, sku, fallback) {
    var labels = getLabels(root);
    var block = document.createElement('div');
    block.id = 'vp-nutrition-' + sku;
    block.className = 'vp-nutrition__block vp-nutrition__block--loading';
    block.setAttribute('data-vp-nutrition-sku', sku);

    // Header can render immediately if the trigger supplied a
    // fallback title/image — no need to wait on the network for
    // that part. It gets rebuilt (and preferred from the API's own
    // variant_title / variant_image, once the backend sends them)
    // in renderBlock().
    block.appendChild(buildHeader(null, sku, fallback));

    var loadingCard = document.createElement('div');
    loadingCard.className = 'vp-nutrition__card';
    loadingCard.innerHTML = '<p class="vp-nutrition__skeleton-text">' +
      escapeHtml(labels.loadingText || 'Loading nutrition facts…') +
      '</p>';
    block.appendChild(loadingCard);

    return block;
  }

  function renderBlock(root, block, data, sku, fallback) {
    var labels = getLabels(root);
    block.classList.remove('vp-nutrition__block--loading');
    block.innerHTML = '';

    block.appendChild(buildHeader(data, sku, fallback));
    block.appendChild(buildCard(data, labels));
    var howTo = buildHowToUse(data, labels);
    if (howTo) block.appendChild(howTo);
    block.appendChild(buildFooter(root));
  }

  // Ensures a block for `sku` exists in the content container and is
  // (or will be) populated. Safe to call multiple times for the same
  // SKU — later calls are no-ops beyond returning the same promise.
  // `fallback` ({title, image}) is only used until the API sends its
  // own variant_title / variant_image on this SKU's response.
  function ensureBlock(root, content, apiRoot, sku, fallback) {
    var existing = content.querySelector('#vp-nutrition-' + CSS.escape(sku));

    if (existing && cache[sku] && cache[sku].status === 'loaded') {
      return Promise.resolve();
    }

    if (!existing) {
      existing = buildSkeleton(root, sku, fallback);
      content.appendChild(existing);
    }

    return fetchNutrition(apiRoot, sku)
      .then(function (data) {
        renderBlock(root, existing, data, sku, fallback);
      })
      .catch(function () {
        // No HTML for a failed/not-found SKU — just drop the
        // skeleton. The sidebar itself is already open by this
        // point (openNutrition opens it before calling this), so
        // the person just sees whatever else is already loaded.
        if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
      });
  }

  // Tabbed mode: [{sku, title, image}] while the sidebar was opened
  // from a tabbed trigger, null otherwise. activeSku is the one block
  // on show in either mode.
  var tabState = null;
  var activeSku = null;

  function showPanel(root) {
    var panel = root.querySelector('[data-vp-nutrition-panel]');
    var overlay = root.querySelector('[data-vp-nutrition-overlay]');
    if (panel) panel.classList.add('vp-open');
    if (overlay) overlay.classList.add('vp-open');
    document.documentElement.classList.add('vp-nutrition-open');
  }

  function clearTabs(root) {
    tabState = null;
    var tabs = root.querySelector('[data-vp-nutrition-tabs]');
    if (tabs) tabs.parentNode.removeChild(tabs);
  }

  function applyVisibility(root) {
    root.querySelectorAll('[data-vp-nutrition-content] .vp-nutrition__block').forEach(function (block) {
      block.hidden = block.getAttribute('data-vp-nutrition-sku') !== activeSku;
    });
    root.querySelectorAll('[data-vp-nutrition-tab]').forEach(function (tab) {
      var selected = tab.getAttribute('data-vp-nutrition-tab') === activeSku;
      tab.classList.toggle('vp-nutrition__tab--active', selected);
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
    });
  }

  // Shows only `sku`'s block (loading it if needed); blocks for other
  // SKUs stay in the DOM, hidden, so reopening them is instant.
  function showSku(root, sku, fallback) {
    var panel = root.querySelector('[data-vp-nutrition-panel]');
    var content = root.querySelector('[data-vp-nutrition-content]');
    var apiRoot = root.getAttribute('data-vp-nutrition-api');
    var error = root.querySelector('[data-vp-nutrition-error]');
    if (error) error.parentNode.removeChild(error);

    activeSku = sku;
    var loaded = ensureBlock(root, content, apiRoot, sku, fallback);
    // ensureBlock adds the skeleton synchronously, so hide the others now.
    applyVisibility(root);
    if (panel) panel.scrollTop = 0;

    loaded.then(function () {
      if (activeSku !== sku) return;
      if (content.querySelector('#vp-nutrition-' + CSS.escape(sku))) return;
      // Fetch failed and ensureBlock dropped the skeleton.
      var labels = getLabels(root);
      var message = document.createElement('p');
      message.className = 'vp-nutrition__error';
      message.setAttribute('data-vp-nutrition-error', '');
      message.textContent = labels.errorText || "We couldn't load nutrition information right now. Please try again.";
      content.appendChild(message);
    });
  }

  function selectTab(root, sku) {
    if (!tabState) return;
    var tab = tabState.filter(function (t) { return t.sku === sku; })[0];
    if (tab) showSku(root, sku, { title: tab.title, image: tab.image });
  }

  function openTabs(tabs, fallbackImage) {
    var root = el('.vp-nutrition--v2');
    if (!root) return;

    tabs = (tabs || []).filter(function (t) { return t && t.sku; }).map(function (t) {
      return { sku: String(t.sku).trim(), title: t.title || t.sku, image: t.image || fallbackImage || null };
    });
    if (!tabs.length) return;

    clearTabs(root);
    tabState = tabs;

    var bar = document.createElement('div');
    bar.className = 'vp-nutrition__tabs';
    bar.setAttribute('role', 'tablist');
    bar.setAttribute('data-vp-nutrition-tabs', '');
    tabs.forEach(function (t) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'vp-nutrition__tab';
      button.setAttribute('role', 'tab');
      button.setAttribute('data-vp-nutrition-tab', t.sku);
      button.textContent = t.title;
      bar.appendChild(button);
    });
    var header = root.querySelector('.vp-nutrition__header');
    if (header) header.insertBefore(bar, header.firstChild);

    showPanel(root);
    selectTab(root, tabs[0].sku);

    // Warm the cache so switching tabs is instant. Data only - blocks
    // are added when a tab is opened.
    var apiRoot = root.getAttribute('data-vp-nutrition-api');
    tabs.slice(1).forEach(function (t) {
      fetchNutrition(apiRoot, t.sku).catch(function () {});
    });
  }

  function openNutrition(sku, fallback) {
    var root = el('.vp-nutrition--v2');
    if (!root) return;

    clearTabs(root);
    showPanel(root);

    if (!sku) {
      var panel = root.querySelector('[data-vp-nutrition-panel]');
      if (panel) panel.scrollTop = 0;
      return;
    }

    showSku(root, sku, fallback);
  }

  function closeNutrition() {
    var root = el('.vp-nutrition--v2');
    if (!root) return;
    var panel = root.querySelector('[data-vp-nutrition-panel]');
    var overlay = root.querySelector('[data-vp-nutrition-overlay]');

    if (panel) panel.classList.remove('vp-open');
    if (overlay) overlay.classList.remove('vp-open');
    document.documentElement.classList.remove('vp-nutrition-open');
  }

  /* ---------------------------------------------------------------------
   * Delegated event handlers — both bound on document, both guarded
   * with a one-time flag on document.body, both resolving the actual
   * element they need via closest() at the moment of the click rather
   * than at init time. This is what keeps them working no matter how
   * many times Vue re-renders the trigger button, the close button, or
   * the overlay. See the file-level comment at the top for why.
   * ------------------------------------------------------------------- */

  function initTogglers() {
    if (document.body.dataset.vpNutritionTogglersDelegated === 'true') return;

    document.addEventListener('click', function (event) {
      var tab = event.target.closest('[data-vp-nutrition-tab]');
      if (tab) {
        var root = el('.vp-nutrition--v2');
        if (root) selectTab(root, tab.getAttribute('data-vp-nutrition-tab'));
        return;
      }

      var tabbed = event.target.closest('[data-vp-nutrition-tabs-v2]');
      if (tabbed) {
        event.preventDefault();
        var tabs;
        try {
          tabs = JSON.parse(tabbed.getAttribute('data-vp-nutrition-tabs-v2'));
        } catch (e) {
          tabs = [];
        }
        openTabs(tabs, (tabbed.getAttribute('data-vp-nutrition-variant-image') || '').trim() || null);
        return;
      }

      var toggler = event.target.closest('[data-vp-nutrition-toggle-v2]');
      if (!toggler) return;

      event.preventDefault();

      var sku = (toggler.getAttribute('data-vp-nutrition-toggle-v2') || '').trim();
      var fallback = {
        title: (toggler.getAttribute('data-vp-nutrition-variant-title') || '').trim() || null,
        image: (toggler.getAttribute('data-vp-nutrition-variant-image') || '').trim() || null
      };
      openNutrition(sku, fallback);
    });

    document.body.dataset.vpNutritionTogglersDelegated = 'true';
  }

  function initCloseTriggers() {
    if (document.body.dataset.vpNutritionCloseDelegated === 'true') return;

    document.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-vp-nutrition-close], [data-vp-nutrition-overlay]');
      if (!trigger) return;
      closeNutrition();
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      var panel = document.querySelector('.vp-nutrition--v2 [data-vp-nutrition-panel]');
      if (panel && panel.classList.contains('vp-open')) closeNutrition();
    });

    document.body.dataset.vpNutritionCloseDelegated = 'true';
  }

  function initAll() {
    initTogglers();
    initCloseTriggers();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);

  window.VivoPebble = window.VivoPebble || {};
  window.VivoPebble.openNutritionV2 = openNutrition;
  window.VivoPebble.closeNutritionV2 = closeNutrition;
})();